import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { authenticationSlice } from '../Authentication/AuthenticationSlice.js'
import type { SpinStatusLoad } from './SpinStatusSlice.ts'
import { pauseOnSessionExpiry } from './SpinStatusSlice.ts'

/**
 * The SPIN metadata the scope's state (`SpinStatusSlice`) does not carry: unclustered counts at an explicit As-of, which
 * the viz panel previews. Its fields are the cache key.
 */
export type SpinMetaQuery = { kind: 'unspinned'; scopeId: string; algorithmType: string; atTime: string }

/** Every field of the query, in a fixed order, so two queries share a key only when they ask the same thing. */
export const spinMetaKey = (query: SpinMetaQuery): string => `${query.kind}|${query.scopeId}|${query.algorithmType}|${query.atTime}`

export interface ISpinMetaEntry {
  scopeId: string
  status: SpinStatusLoad
  /** Untyped here; the hook owning the query kind casts it back. Kept through an invalidation and a failed refetch. */
  data?: unknown
  /** Tags the fetch in flight; a response for an older request is dropped. */
  requestId?: string
}

export interface ISpinMetaState {
  entries: Record<string, ISpinMetaEntry>
}

const initialState: ISpinMetaState = { entries: {} }

/** Unclustered counts kept per scope: every As of the viz panel previews adds one. */
export const MAX_UNSPINNED_PER_SCOPE = 8

/** Drops the least recently fetched unclustered counts of a scope beyond the cap; a fetch in flight is never dropped. */
const evictUnspinned = (entries: Record<string, ISpinMetaEntry>, scopeId: string) => {
  const prefix = `unspinned|${scopeId}|`
  // Insertion order is fetch order: a started fetch re-inserts its key.
  const keys = Object.keys(entries).filter((key) => key.startsWith(prefix))
  let excess = keys.length - MAX_UNSPINNED_PER_SCOPE
  for (const key of keys) {
    if (excess <= 0) {
      return
    }
    if (entries[key]!.status === 'loading') {
      continue
    }
    delete entries[key]
    excess--
  }
}

export const spinMetaSlice = createSlice({
  name: 'spinMeta',
  initialState,
  reducers: {
    /** Epic-only: whether this fetches depends on the cached entry, a decision the epic alone makes. */
    requestSpinMeta: {
      reducer: (state: ISpinMetaState) => state,
      prepare: (payload: { query: SpinMetaQuery }) => ({ payload })
    },
    requestSpinMetaStarted: (state, action: PayloadAction<{ key: string; scopeId: string; requestId: string }>) => {
      const { key, scopeId, requestId } = action.payload
      const data = state.entries[key]?.data
      delete state.entries[key]
      state.entries[key] = { scopeId, status: 'loading', data, requestId }
      evictUnspinned(state.entries, scopeId)
    },
    requestSpinMetaSuccess: (state, action: PayloadAction<{ key: string; requestId: string; data: unknown }>) => {
      const entry = state.entries[action.payload.key]
      if (entry?.requestId !== action.payload.requestId) {
        return
      }
      state.entries[action.payload.key] = { scopeId: entry.scopeId, status: 'ready', data: action.payload.data }
    },
    requestSpinMetaFailure: (state, action: PayloadAction<{ key: string; requestId: string; error: unknown }>) => {
      const entry = state.entries[action.payload.key]
      if (entry?.requestId !== action.payload.requestId) {
        return
      }
      state.entries[action.payload.key] = { scopeId: entry.scopeId, status: 'error', data: entry.data }
    },
    /**
     * Marks the answers of these scopes stale (all scopes when omitted); mounted consumers refetch, a fetch in flight is
     * superseded. Dispatched when a scope's state comes back with a new tag.
     */
    invalidateSpinMeta: (state, action: PayloadAction<{ scopeIds?: string[] } | undefined>) => {
      const ids = action.payload?.scopeIds
      for (const [key, entry] of Object.entries(state.entries)) {
        if (ids === undefined || ids.includes(entry.scopeId)) {
          state.entries[key] = { scopeId: entry.scopeId, status: 'idle', data: entry.data }
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(authenticationSlice.actions.authenticationExpired, (state) => {
      pauseOnSessionExpiry(state.entries)
    })
  }
})
